import React, { useEffect, useState } from 'react';
import {
  Button,
  Text,
  VStack,
  HStack,
  LoadingSpinner,
  Card,
  Highlight,
} from '@adc/parallax-component-library';
import {
  IconBannersInfo,
  IconBannersWarning,
  IconCheckCircle,
  IconDownload,
} from '@adc/parallax-icons';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import { DownloadStatus } from '../../constants';
import { ProgressBar } from '../progressBar/ProgressBar';
import { ConfirmationModal } from './ConfirmationModal';
import './Modal.scss';

export interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  studyName: string;
  status: DownloadStatus;
  progress?: number;
  downloadUrl?: string;
  filename?: string;
  errorMessage?: string;
}

/**
 * Modal for showing export progress and providing download link for study data.
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {function} onClose - Callback function to close the modal
 * @param {string} studyName - Name of the study being exported
 * @param {string} status - Current status of the export process
 * @param {number} progress - Optional progress percentage (0-100)
 * @param {string} downloadUrl - Optional URL for the completed download
 * @param {string} filename - Optional filename of the download
 * @param {string} errorMessage - Optional error message if export failed
 * @returns {React.JSX.Element}
 */
export default function DownloadModal({
  isOpen,
  onClose,
  studyName,
  status,
  progress,
  downloadUrl,
  filename,
  errorMessage,
}: DownloadModalProps) {
  const isDownloadActive =
    status === DownloadStatus.PREPARING || status === DownloadStatus.PROCESSING;
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // Prevent browser unload during active exports
  // TODO: do we want to cancel the active job via the API?
  useEffect(() => {
    if (!isDownloadActive) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return `${i18n.t('Modal_Download.active_body')}`;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDownloadActive]);

  const handleCloseAttempt = () => {
    if (isDownloadActive) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const handleDownloadClick = () => {
    if (downloadUrl && !isButtonDisabled) {
      setIsButtonDisabled(true);

      try {
        // Create a temporary link and trigger download
        const link = document.createElement('a');

        link.href = downloadUrl;
        link.download = filename || 'download.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        setIsButtonDisabled(false);
      }
    }
  };

  const renderStatusBanner = () => {
    switch (status) {
      case DownloadStatus.FAILED:
        return (
          <Highlight
            variant="danger"
            iconStart={
              <IconBannersWarning
                color="$neutral.0"
                accentColor="$support.danger.default"
              />
            }
            title={i18n.t('Modal_Download.failed_title')}
            body={errorMessage || i18n.t('Modal_Download.error_generic_failed')}
          />
        );
      case DownloadStatus.PREPARING:
        return (
          <Highlight
            variant="informational"
            iconStart={
              <IconBannersInfo
                color="$neutral.0"
                accentColor="$support.informational.default"
              />
            }
            title={i18n.t('Modal_Download.preparing_title')}
            body={i18n.t('Modal_Download.active_body')}
          />
        );
      case DownloadStatus.PROCESSING:
        return (
          <Highlight
            variant="informational"
            iconStart={
              <IconBannersInfo
                color="$neutral.0"
                accentColor="$support.informational.default"
              />
            }
            title={i18n.t('Modal_Download.processing_title')}
            body={i18n.t('Modal_Download.active_body')}
          />
        );
      case DownloadStatus.COMPLETED:
        return (
          <Highlight
            variant="success"
            iconStart={
              <IconCheckCircle
                color="$neutral.0"
                accentColor="$support.positive.default"
              />
            }
            title={i18n.t('Modal_Download.completed_description')}
            body={i18n.t('Modal_Download.completed_body')}
          />
        );
      default:
        return null;
    }
  };

  const renderDownloadSection = () => {
    if (status !== DownloadStatus.COMPLETED) {
      return null;
    }

    if (!filename) {
      return (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Modal_Download.failed_title')}
          body={i18n.t('Modal_Download.error_missing_file')}
        />
      );
    }

    return (
      <Card>
        <VStack gap="$1">
          <Text type="$bodyBase.strong">
            {i18n.t('Modal_Download.exported_file_label')}
          </Text>
          <HStack gap="$2" alignItems="center" justifyContent="space-between">
            <Text type="$bodySmall.default">{filename}</Text>
            {downloadUrl ? (
              <Button
                variant="primary"
                size="small"
                label={i18n.t('Modal_Download.download_file')}
                iconStart={<IconDownload />}
                onPress={handleDownloadClick}
                disabled={isButtonDisabled}
              />
            ) : null}
          </HStack>
        </VStack>
      </Card>
    );
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content" width="50%">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">{i18n.t('Modal_Download.title')}</Text>
            </Dialog.Title>
          </VStack>

          <VStack
            gap="$4"
            paddingVertical="$4"
            flex={1}
            minHeight={0}
            overflow="scroll"
          >
            <VStack gap="$1">
              <Text type="$bodyBase.strong">
                {i18n.t('Modal_Download.study_label')}
              </Text>
              <Text type="$bodyBase.default">{studyName}</Text>
            </VStack>

            {renderStatusBanner()}

            {status !== DownloadStatus.IDLE && progress !== undefined ? (
              <VStack gap="$3" alignItems="center">
                {status === DownloadStatus.PREPARING ? (
                  <LoadingSpinner />
                ) : null}

                <VStack gap="$2" width="100%">
                  <ProgressBar
                    progress={progress}
                    color={
                      status === DownloadStatus.FAILED
                        ? '$neutral.40'
                        : '$abbott.lightBlue.default'
                    }
                    testId="download-progress-bar"
                  />
                  <Text textAlign="center" type="$bodySmall.default">
                    {Math.round(progress)}
                    {i18n.t('Modal_Download.progress_complete')}
                  </Text>
                </VStack>
              </VStack>
            ) : null}

            {renderDownloadSection()}
          </VStack>

          <HStack justifyContent="flex-end" gap="$3" flexShrink={0}>
            <Button
              variant="secondary"
              size="medium"
              label={
                status === DownloadStatus.FAILED ||
                status === DownloadStatus.COMPLETED
                  ? i18n.t('Common.close')
                  : i18n.t('Common.cancel')
              }
              onPress={handleCloseAttempt}
            />
          </HStack>
        </Dialog.Content>
      </Dialog.Portal>

      <ConfirmationModal
        isOpen={showConfirmClose}
        title={i18n.t('Modal_Download.confirm_close_title')}
        description={i18n.t('Modal_Download.active_body')}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        showCancelButton={true}
      />
    </Dialog>
  );
}
